/**
 * OCC workbench pure helpers (S4.3) — no React, no I/O.
 *
 * Everything the lenses need to decide WITHOUT rendering: the 鑽取來源 drill
 * route per exception reason (write-back always happens in the owning
 * module — OCC never edits), snooze expiry (snoozes expire; there is no
 * manual close), and the Order Lines client-side filter / SO-PO grouping.
 * Vitest target: `src/components/occ/__tests__/occWorkbench.test.ts`.
 */

import { filterLinesByProduct } from '@/lib/occ/selectors';
import type { OccExceptionReason, OccExceptionRow, OccLensLine } from '@/lib/occ/selectors';
import type { ExceptionSnooze, OccLinesFilters } from '@/stores/occStore';

// ---------------------------------------------------------------------------
// 鑽取來源 drill routing — exception → owning module route
// ---------------------------------------------------------------------------

/**
 * Where write-back for a reason lives:
 * - price_review — the deviation hold is released on the SO detail (S2.3);
 * - back_order / delay — the buyer chases supply on the PO detail (or raises
 *   one from the demand pool when no PO exists yet);
 * - short_load / overflow — container facts are reconciled on 裝櫃批次.
 */
export function drillRouteForException(
  row: Pick<OccExceptionRow, 'reason' | 'soId'>,
  poLineIds: string[],
  poIdOf: (poLineId: string) => string | undefined
): string {
  switch (row.reason) {
    case 'price_review':
      return `/auth/sales-orders/${row.soId}`;
    case 'short_load':
    case 'overflow':
      return '/auth/shipments';
    case 'back_order':
    case 'delay': {
      for (const poLineId of poLineIds) {
        const poId = poIdOf(poLineId);
        if (poId) return `/auth/purchase-orders/${poId}`;
      }
      return '/auth/purchase-orders/demand-pool';
    }
  }
}

// ---------------------------------------------------------------------------
// Snooze — with-reason, EXPIRES (no manual close anywhere)
// ---------------------------------------------------------------------------

/** Duration presets offered in the snooze modal (hours). */
export const SNOOZE_DURATION_HOURS = [24, 48, 72, 168] as const;
export type SnoozeDurationHours = (typeof SNOOZE_DURATION_HOURS)[number];

export function snoozeUntil(nowIso: string, hours: number): string {
  return new Date(Date.parse(nowIso) + hours * 3_600_000).toISOString();
}

/** Active = an unexpired snooze exists. Expired snoozes resurface the row. */
export function isSnoozeActive(
  snooze: Pick<ExceptionSnooze, 'until'> | undefined,
  nowIso: string
): boolean {
  return Boolean(snooze && Date.parse(snooze.until) > Date.parse(nowIso));
}

// ---------------------------------------------------------------------------
// Order Lines lens — client-side filter + SO/PO 對照 grouping
// ---------------------------------------------------------------------------

/** Case-insensitive match over the identity/context columns the grid shows. */
export function lineMatchesSearch(line: OccLensLine, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [
    line.sku,
    line.productName,
    line.internalProductId,
    line.scn ?? '',
    line.soNumber,
    line.customerName,
    line.vendorName ?? '',
  ].some((value) => value.toLowerCase().includes(needle));
}

/**
 * Apply the lens filters. `flaggedLineIds` comes from the exception worklist
 * (derived rows), so 僅顯示異常 shows exactly the lines the engine flags.
 *
 * `productId` (SR2.2) is the route-derived `?productId=` scope — the canonical
 * `internalProductId` of the deep-linked product (SR3.5's 產品管控中心 links).
 * It rides through the SAME `filterLinesByProduct` predicate the report and the
 * board totals use, so a product-scoped lens can never fork the match rule; a
 * blank id is the unscoped case and matches every line.
 */
export function filterOccLines(
  lines: OccLensLine[],
  filters: OccLinesFilters,
  flaggedLineIds: ReadonlySet<string>,
  productId?: string | null
): OccLensLine[] {
  return filterLinesByProduct(lines, productId).filter((line) => {
    if (!lineMatchesSearch(line, filters.search)) return false;
    if (filters.node !== 'all' && line.node !== filters.node) return false;
    if (filters.owner !== 'all' && line.owner.role !== filters.owner) return false;
    if (filters.exceptionsOnly && !flaggedLineIds.has(line.id)) return false;
    if (filters.balanceOnly && line.qtyOpenToLoad <= 0) return false;
    return true;
  });
}

/** One node of the SO/PO 對照 tree: an SO parent or one of its lines. */
export interface SoPoGroupRow {
  key: string;
  kind: 'so' | 'line';
  /** Present on both kinds — parents carry the group's first line's context. */
  soId: string;
  soNumber: string;
  customerName: string;
  /** Parent aggregates (kind 'so'). */
  lineCount?: number;
  qtyOrdered?: number;
  qtyConfirmed?: number;
  qtyLoaded?: number;
  qtyReceived?: number;
  orderBalance?: number;
  /** The projected line (kind 'line'). */
  line?: OccLensLine;
  children?: SoPoGroupRow[];
}

/** Group filtered lines by SO for the 對照 toggle (aggregates roll up). */
export function groupLinesBySo(lines: OccLensLine[]): SoPoGroupRow[] {
  const bySo = new Map<string, OccLensLine[]>();
  lines.forEach((line) => {
    const group = bySo.get(line.soId);
    if (group) group.push(line);
    else bySo.set(line.soId, [line]);
  });
  return [...bySo.entries()]
    .map(([soId, group]) => ({
      key: `so:${soId}`,
      kind: 'so' as const,
      soId,
      soNumber: group[0].soNumber,
      customerName: group[0].customerName,
      lineCount: group.length,
      qtyOrdered: group.reduce((sum, line) => sum + line.qtyOrdered, 0),
      qtyConfirmed: group.reduce((sum, line) => sum + line.qtyConfirmed, 0),
      qtyLoaded: group.reduce((sum, line) => sum + line.qtyLoaded, 0),
      qtyReceived: group.reduce((sum, line) => sum + line.qtyReceived, 0),
      orderBalance: group.reduce((sum, line) => sum + line.orderBalance, 0),
      children: group.map((line) => ({
        key: `line:${line.id}`,
        kind: 'line' as const,
        soId: line.soId,
        soNumber: line.soNumber,
        customerName: line.customerName,
        line,
      })),
    }))
    .sort((a, b) => a.soNumber.localeCompare(b.soNumber));
}

/** Count of active (non-default) popover filters — the filter-badge number. */
export function countLinesFilterBadges(filters: OccLinesFilters): number {
  let count = 0;
  if (filters.node !== 'all') count += 1;
  if (filters.owner !== 'all') count += 1;
  if (filters.exceptionsOnly) count += 1;
  if (filters.balanceOnly) count += 1;
  return count;
}

/**
 * Segmented worklist rows after reason filter + snooze visibility.
 *
 * `productId` (SR2.2) applies the route-derived `?productId=` scope through the
 * shared `filterLinesByProduct` predicate (exception rows carry the same
 * canonical `internalProductId` as lens lines); a blank id matches every row.
 */
export function filterExceptionRows(
  rows: OccExceptionRow[],
  reason: 'all' | OccExceptionReason,
  showSnoozed: boolean,
  snoozes: Record<string, ExceptionSnooze>,
  nowIso: string,
  productId?: string | null
): OccExceptionRow[] {
  return filterLinesByProduct(rows, productId).filter((row) => {
    if (reason !== 'all' && row.reason !== reason) return false;
    if (!showSnoozed && isSnoozeActive(snoozes[row.id], nowIso)) return false;
    return true;
  });
}
